
// src/pages/VisitReport/CreateVisitReportPage.js
// New customer visit report with live duplicate / revisit detection.
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapPin, Send } from 'lucide-react';
import {
  createVisitReport,
  checkDuplicateVisits,
} from '../../services/features/visitReport/visitReportSlice';
import { Button, toast } from '../../components/ui/UI';
import {
  FLOW_META,
  formatDateKey,
  formatHumanDate,
  ordinal,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const INITIAL = {
  name: '',
  businessName: '',
  mobile: '',
  taluk: '',      // entered manually for every report
  district: '',   // entered manually for every report
  business: '',
  status: 'VISITED',
  statusFlow: 'WARM',
  report: '',
};

const times = (n) => `${n} visit${n > 1 ? 's' : ''}`;

const TextField = ({ label, error, hint, ...props }) => (
  <div>
    <div className="rp-field-label">{label}</div>
    <input
      className="rp-input"
      style={{ width: '100%', marginTop: 4, borderColor: error ? 'var(--red)' : undefined }}
      {...props}
    />
    {error && <div style={{ color: 'var(--red-bright)', fontSize: 12, marginTop: 4 }}>{error}</div>}
    {!error && hint && <div className="rp-hint">{hint}</div>}
  </div>
);

const CreateVisitReportPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { submitting } = useSelector((s) => s.visitReport);

  const [form, setForm] = useState({ ...INITIAL });
  const [errors, setErrors] = useState({});
  const [dupInfo, setDupInfo] = useState(null);
  const [loc, setLoc] = useState(null);
  const [locBusy, setLocBusy] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const n = { ...e };
      delete n[key];
      return n;
    });
  };

  // ── Duplicate check: mobile / name / shop — each on its own ─────
  useEffect(() => {
    const params = {
      mobile: /^[6-9]\d{9}$/.test(form.mobile) ? form.mobile : '',
      name: form.name.trim().length >= 3 ? form.name.trim() : '',
      businessName: form.businessName.trim().length >= 3 ? form.businessName.trim() : '',
    };
    if (!params.mobile && !params.name && !params.businessName) {
      setDupInfo(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await dispatch(checkDuplicateVisits(params)).unwrap();
        if (!cancelled) setDupInfo(res?.visits?.length ? res : null);
      } catch {
        if (!cancelled) setDupInfo(null);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.mobile, form.name, form.businessName, dispatch]);

  const dm = dupInfo?.matches || { mobile: 0, name: 0, businessName: 0 };
  const strongCount = Math.max(dm.mobile, dm.businessName);

  // ── Location (browser GPS, optional) ────────────────────────────
  const captureLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.warn('This browser cannot provide location');
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoc({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy || 0,
        });
        setLocBusy(false);
      },
      () => {
        setLocBusy(false);
        toast.warn('Location unavailable — you can still submit without it');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
  }, []);

  // ── Validate + submit ───────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.businessName.trim()) e.businessName = 'Business name is required';
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile';
    if (!form.taluk.trim()) e.taluk = 'Taluk is required';
    if (!form.district.trim()) e.district = 'District is required';
    if (!form.business.trim()) e.business = 'Business is required';
    if (!form.report.trim()) e.report = 'Report is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting || !validate()) return;
    const payload = {
      ...form,
      mobile: form.mobile.trim(),
      latitude: loc?.latitude,
      longitude: loc?.longitude,
      accuracy: loc?.accuracy,
    };
    try {
      const created = await dispatch(createVisitReport(payload)).unwrap();
      toast.success(
        created?.visitNumber > 1
          ? `Recorded as the ${ordinal(created.visitNumber)} visit to this customer`
          : 'Customer visit recorded'
      );
      navigate('/visit-reports/list');
    } catch (e) {
      toast.error(typeof e === 'string' ? e : e?.message || 'Failed to submit');
    }
  };

  return (
    <form className="rp-page" style={{ maxWidth: 860 }} onSubmit={submit}>
      {/* Customer details */}
      <div className="rp-card rp-card-pad">
        <h3 className="rp-card-title">Customer Details</h3>
        <div className="rp-form-grid">
          <TextField
            label="Name *"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            error={errors.name}
            hint={dm.name > 0 ? `Same customer name found in ${times(dm.name)}` : null}
            placeholder="Customer / contact person"
          />
          <TextField
            label="Business Name *"
            value={form.businessName}
            onChange={(e) => set('businessName', e.target.value)}
            error={errors.businessName}
            hint={dm.businessName > 0 ? `This shop was already visited ${times(dm.businessName)}` : null}
            placeholder="Shop / firm name"
          />
          <TextField
            label="Mobile Number *"
            value={form.mobile}
            onChange={(e) => set('mobile', e.target.value.replace(/\D/g, ''))}
            error={errors.mobile}
            hint={dm.mobile > 0 ? `This mobile number is already in ${times(dm.mobile)}` : null}
            placeholder="10-digit mobile"
            inputMode="numeric"
            maxLength={10}
          />
          <TextField
            label="Business *"
            value={form.business}
            onChange={(e) => set('business', e.target.value)}
            error={errors.business}
            placeholder="What business do they run?"
          />
          <TextField
            label="Taluk *"
            value={form.taluk}
            onChange={(e) => set('taluk', e.target.value)}
            error={errors.taluk}
            placeholder="Taluk"
          />
          <TextField
            label="District *"
            value={form.district}
            onChange={(e) => set('district', e.target.value)}
            error={errors.district}
            placeholder="District"
          />
        </div>

        {dupInfo && (
          <div className="rp-revisitbox" style={{ marginTop: 16 }}>
            {strongCount > 0
              ? `REVISIT · This will be the ${ordinal(strongCount + 1)} visit`
              : 'SIMILAR CUSTOMER NAME FOUND'}
            <div className="rp-visits" style={{ fontWeight: 500 }}>
              {dupInfo.visits.slice(0, 3).map((v) => (
                <div key={v._id}>
                  • {formatHumanDate(formatDateKey(v.submittedAt))} ·{' '}
                  {v.byMe
                    ? 'by you'
                    : v.visitedBy
                    ? `by ${v.visitedBy}${v.visitedByRole ? ` (${v.visitedByRole})` : ''}`
                    : 'by —'}{' '}
                  · {v.businessName} · {v.statusFlow}
                  {v.matchedOn?.length
                    ? `  [${v.matchedOn
                        .map((m) => (m === 'shop' ? 'Shop' : m === 'name' ? 'Name' : 'Mobile'))
                        .join(' + ')}]`
                    : ''}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lead status */}
      <div className="rp-card rp-card-pad">
        <h3 className="rp-card-title">Lead Status *</h3>
        <div className="rp-flow">
          {Object.entries(FLOW_META).map(([k, m]) => (
            <button
              key={k}
              type="button"
              className={`rp-flowbtn ${form.statusFlow === k ? 'active' : ''}`}
              style={{ '--c': m.color, '--cbg': m.bg }}
              onClick={() => set('statusFlow', k)}
            >
              <b>{m.label.toUpperCase()}</b>
              <small>{m.hint}</small>
            </button>
          ))}
        </div>
      </div>

      {/* Report */}
      <div className="rp-card rp-card-pad">
        <h3 className="rp-card-title">Report *</h3>
        <textarea
          className="rp-input"
          style={{
            width: '100%', height: 140, padding: 12, resize: 'vertical',
            borderColor: errors.report ? 'var(--red)' : undefined,
          }}
          value={form.report}
          onChange={(e) => set('report', e.target.value)}
          placeholder="Write what happened during the visit, customer needs, next steps…"
        />
        {errors.report && (
          <div style={{ color: 'var(--red-bright)', fontSize: 12, marginTop: 4 }}>{errors.report}</div>
        )}
      </div>

      {/* Location */}
      <div className="rp-card rp-card-pad">
        <div className="rp-toolbar">
          <h3 className="rp-card-title" style={{ margin: 0 }}>Location</h3>
          <div className="rp-spacer" />
          <Button type="button" size="sm" variant="ghost" loading={locBusy} onClick={captureLocation}>
            {loc ? 'Refresh' : 'Capture location'}
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
          <MapPin size={16} color={loc ? '#10b981' : 'var(--text-muted)'} />
          {loc
            ? `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)} (±${Math.round(loc.accuracy)}m)`
            : 'Location not captured — optional'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
        <Button type="submit" loading={submitting} icon={<Send size={15} />}>
          Submit Report
        </Button>
      </div>
    </form>
  );
};

export default CreateVisitReportPage;


//------------- 13.10.2026 Backup --------------------
// // src/pages/VisitReport/CreateVisitReportPage.js
// // New customer visit report with live duplicate / revisit detection.
// import React, { useCallback, useEffect, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { MapPin, Send } from 'lucide-react';
// import {
//   createVisitReport,
//   checkDuplicateVisits,
// } from '../../services/features/visitReport/visitReportSlice';
// import { Button, toast } from '../../components/ui/UI';
// import {
//   FLOW_META,
//   formatDateKey,
//   formatHumanDate,
//   ordinal,
// } from '../../utils/attendanceFormat';
// import '../Shared/ReportPages.css';

// const INITIAL = {
//   name: '',
//   businessName: '',
//   mobile: '',
//   taluk: '',      // entered manually for every report
//   district: '',   // entered manually for every report
//   business: '',
//   status: 'VISITED',
//   statusFlow: 'WARM',
//   report: '',
// };

// const times = (n) => `${n} visit${n > 1 ? 's' : ''}`;

// const TextField = ({ label, error, hint, ...props }) => (
//   <div>
//     <div className="rp-field-label">{label}</div>
//     <input
//       className="rp-input"
//       style={{ width: '100%', marginTop: 4, borderColor: error ? 'var(--red)' : undefined }}
//       {...props}
//     />
//     {error && <div style={{ color: 'var(--red-bright)', fontSize: 12, marginTop: 4 }}>{error}</div>}
//     {!error && hint && <div className="rp-hint">{hint}</div>}
//   </div>
// );

// const CreateVisitReportPage = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { submitting } = useSelector((s) => s.visitReport);

//   const [form, setForm] = useState({ ...INITIAL });
//   const [errors, setErrors] = useState({});
//   const [dupInfo, setDupInfo] = useState(null);
//   const [loc, setLoc] = useState(null);
//   const [locBusy, setLocBusy] = useState(false);

//   const set = (key, value) => {
//     setForm((f) => ({ ...f, [key]: value }));
//     setErrors((e) => {
//       if (!e[key]) return e;
//       const n = { ...e };
//       delete n[key];
//       return n;
//     });
//   };

//   // ── Duplicate check: mobile / name / shop — each on its own ─────
//   useEffect(() => {
//     const params = {
//       mobile: /^[6-9]\d{9}$/.test(form.mobile) ? form.mobile : '',
//       name: form.name.trim().length >= 3 ? form.name.trim() : '',
//       businessName: form.businessName.trim().length >= 3 ? form.businessName.trim() : '',
//     };
//     if (!params.mobile && !params.name && !params.businessName) {
//       setDupInfo(null);
//       return undefined;
//     }
//     let cancelled = false;
//     const t = setTimeout(async () => {
//       try {
//         const res = await dispatch(checkDuplicateVisits(params)).unwrap();
//         if (!cancelled) setDupInfo(res?.visits?.length ? res : null);
//       } catch {
//         if (!cancelled) setDupInfo(null);
//       }
//     }, 600);
//     return () => {
//       cancelled = true;
//       clearTimeout(t);
//     };
//   }, [form.mobile, form.name, form.businessName, dispatch]);

//   const dm = dupInfo?.matches || { mobile: 0, name: 0, businessName: 0 };
//   const strongCount = Math.max(dm.mobile, dm.businessName);

//   // ── Location (browser GPS, optional) ────────────────────────────
//   const captureLocation = useCallback(() => {
//     if (!navigator.geolocation) {
//       toast.warn('This browser cannot provide location');
//       return;
//     }
//     setLocBusy(true);
//     navigator.geolocation.getCurrentPosition(
//       (pos) => {
//         setLoc({
//           latitude: pos.coords.latitude,
//           longitude: pos.coords.longitude,
//           accuracy: pos.coords.accuracy || 0,
//         });
//         setLocBusy(false);
//       },
//       () => {
//         setLocBusy(false);
//         toast.warn('Location unavailable — you can still submit without it');
//       },
//       { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
//     );
//   }, []);

//   // ── Validate + submit ───────────────────────────────────────────
//   const validate = () => {
//     const e = {};
//     if (!form.name.trim()) e.name = 'Name is required';
//     if (!form.businessName.trim()) e.businessName = 'Business name is required';
//     if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile';
//     if (!form.taluk.trim()) e.taluk = 'Taluk is required';
//     if (!form.district.trim()) e.district = 'District is required';
//     if (!form.business.trim()) e.business = 'Business is required';
//     if (!form.report.trim()) e.report = 'Report is required';
//     setErrors(e);
//     return Object.keys(e).length === 0;
//   };

//   const submit = async (ev) => {
//     ev.preventDefault();
//     if (submitting || !validate()) return;
//     const payload = {
//       ...form,
//       mobile: form.mobile.trim(),
//       latitude: loc?.latitude,
//       longitude: loc?.longitude,
//       accuracy: loc?.accuracy,
//     };
//     try {
//       const created = await dispatch(createVisitReport(payload)).unwrap();
//       toast.success(
//         created?.visitNumber > 1
//           ? `Recorded as the ${ordinal(created.visitNumber)} visit to this customer`
//           : 'Customer visit recorded'
//       );
//       navigate('/visit-reports/list');
//     } catch (e) {
//       toast.error(typeof e === 'string' ? e : e?.message || 'Failed to submit');
//     }
//   };

//   return (
//     <form className="rp-page" style={{ maxWidth: 860 }} onSubmit={submit}>
//       {/* Customer details */}
//       <div className="rp-card rp-card-pad">
//         <h3 className="rp-card-title">Customer Details</h3>
//         <div className="rp-form-grid">
//           <TextField
//             label="Name *"
//             value={form.name}
//             onChange={(e) => set('name', e.target.value)}
//             error={errors.name}
//             hint={dm.name > 0 ? `Same customer name found in ${times(dm.name)}` : null}
//             placeholder="Customer / contact person"
//           />
//           <TextField
//             label="Business Name *"
//             value={form.businessName}
//             onChange={(e) => set('businessName', e.target.value)}
//             error={errors.businessName}
//             hint={dm.businessName > 0 ? `This shop was already visited ${times(dm.businessName)}` : null}
//             placeholder="Shop / firm name"
//           />
//           <TextField
//             label="Mobile Number *"
//             value={form.mobile}
//             onChange={(e) => set('mobile', e.target.value.replace(/\D/g, ''))}
//             error={errors.mobile}
//             hint={dm.mobile > 0 ? `This mobile number is already in ${times(dm.mobile)}` : null}
//             placeholder="10-digit mobile"
//             inputMode="numeric"
//             maxLength={10}
//           />
//           <TextField
//             label="Business *"
//             value={form.business}
//             onChange={(e) => set('business', e.target.value)}
//             error={errors.business}
//             placeholder="What business do they run?"
//           />
//           <TextField
//             label="Taluk *"
//             value={form.taluk}
//             onChange={(e) => set('taluk', e.target.value)}
//             error={errors.taluk}
//             placeholder="Taluk"
//           />
//           <TextField
//             label="District *"
//             value={form.district}
//             onChange={(e) => set('district', e.target.value)}
//             error={errors.district}
//             placeholder="District"
//           />
//         </div>

//         {dupInfo && (
//           <div className="rp-revisitbox" style={{ marginTop: 16 }}>
//             {strongCount > 0
//               ? `REVISIT · This will be the ${ordinal(strongCount + 1)} visit`
//               : 'SIMILAR CUSTOMER NAME FOUND'}
//             <div className="rp-visits" style={{ fontWeight: 500 }}>
//               {dupInfo.visits.slice(0, 3).map((v) => (
//                 <div key={v._id}>
//                   • {formatHumanDate(formatDateKey(v.submittedAt))} ·{' '}
//                   {v.byMe
//                     ? 'by you'
//                     : v.visitedBy
//                     ? `by ${v.visitedBy}${v.visitedByRole ? ` (${v.visitedByRole})` : ''}`
//                     : 'by —'}{' '}
//                   · {v.businessName} · {v.statusFlow}
//                   {v.matchedOn?.length
//                     ? `  [${v.matchedOn
//                         .map((m) => (m === 'shop' ? 'Shop' : m === 'name' ? 'Name' : 'Mobile'))
//                         .join(' + ')}]`
//                     : ''}
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Lead status */}
//       <div className="rp-card rp-card-pad">
//         <h3 className="rp-card-title">Lead Status *</h3>
//         <div className="rp-flow">
//           {Object.entries(FLOW_META).map(([k, m]) => (
//             <button
//               key={k}
//               type="button"
//               className={`rp-flowbtn ${form.statusFlow === k ? 'active' : ''}`}
//               style={{ '--c': m.color, '--cbg': m.bg }}
//               onClick={() => set('statusFlow', k)}
//             >
//               <b>{m.label.toUpperCase()}</b>
//               <small>{m.hint}</small>
//             </button>
//           ))}
//         </div>
//       </div>

//       {/* Report */}
//       <div className="rp-card rp-card-pad">
//         <h3 className="rp-card-title">Report *</h3>
//         <textarea
//           className="rp-input"
//           style={{
//             width: '100%', height: 140, padding: 12, resize: 'vertical',
//             borderColor: errors.report ? 'var(--red)' : undefined,
//           }}
//           value={form.report}
//           onChange={(e) => set('report', e.target.value)}
//           placeholder="Write what happened during the visit, customer needs, next steps…"
//         />
//         {errors.report && (
//           <div style={{ color: 'var(--red-bright)', fontSize: 12, marginTop: 4 }}>{errors.report}</div>
//         )}
//       </div>

//       {/* Location */}
//       <div className="rp-card rp-card-pad">
//         <div className="rp-toolbar">
//           <h3 className="rp-card-title" style={{ margin: 0 }}>Location</h3>
//           <div className="rp-spacer" />
//           <Button type="button" size="sm" variant="ghost" loading={locBusy} onClick={captureLocation}>
//             {loc ? 'Refresh' : 'Capture location'}
//           </Button>
//         </div>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
//           <MapPin size={16} color={loc ? '#10b981' : 'var(--text-muted)'} />
//           {loc
//             ? `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)} (±${Math.round(loc.accuracy)}m)`
//             : 'Location not captured — optional'}
//         </div>
//       </div>

//       <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
//         <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
//         <Button type="submit" loading={submitting} icon={<Send size={15} />}>
//           Submit Report
//         </Button>
//       </div>
//     </form>
//   );
// };

// export default CreateVisitReportPage;
